import React from "react";
import { useDeal } from "../lib/store";
import { Tile, Card, ScopeTag, ScenarioBar } from "../components/shared";
import { SourcesUsesTables, suBreakdown } from "../components/SourcesUsesTables";
import { TransactionCostsCard, CapitalisationCard } from "../components/FinancialBlocks";
import * as m from "../lib/model";
import { AuditBadge } from "../components/AuditBadge";

export default function SourcesUses() {
  const { M, V, scope, navigate, scenario, setScenario, scenFlash } = useDeal();
  const consolidated = m.isRollupScope(V);
  const viewTag = m.scopeLabel(M, scope);
  const { su, totalDebt, totalEquity } = suBreakdown(V);
  const off = Math.abs(su.gap) > 1;
  const pctOf = (x) => (su.totalSources ? (x / su.totalSources) * 100 : 0);
  const debtPct = pctOf(totalDebt);
  const tc = m.transactionCosts(V);
  const ac = consolidated ? m.addonCosts(V) : { fees: 0 };
  const tcTotal = tc.total + (V.transExpenses || 0) + (consolidated ? ac.fees : 0);
  const recap = m.isRecap(V);

  return (
    <div className={`page ${scenFlash ? "scen-flash" : ""}`} data-testid="page-sourcesuses">
      <div className="page-head">
        <div className="page-title">Sources &amp; Uses{viewTag && <span className="view-tag" data-testid="su-view-tag">{viewTag}</span>}</div>
        <div className="page-sub">Every dollar into the deal and where it comes from — {consolidated ? "consolidated across the platform and all scheduled add-ons" : recap ? "recapitalisation structure" : "this deal on its own"}.</div>
      </div>

      <div className="dash-ctlbar" data-testid="su-controls">
        <ScopeTag M={M} value={scope} onEdit={() => navigate("assumptions")} testid="su-scope" />
        <AuditBadge V={V} />
        <ScenarioBar value={scenario} onChange={setScenario} />
      </div>

      <div className="grid g4" style={{ marginBottom: 12 }}>
        <Tile label="Total Uses" value={m.fmt$(su.totalUses)} sub={recap ? "Recap uses" : "Purchase price + costs"} testid="su-tile-uses" />
        <Tile label="Total Sources" value={m.fmt$(su.totalSources)} sub={off ? `Out of balance by ${m.fmt$(su.gap)}` : "Balanced"} tone={off ? "pass" : "go"} testid="su-tile-sources" />
        <Tile label="Total Debt" value={m.fmt$(totalDebt)} sub={`${debtPct.toFixed(1)}% of capitalisation`} testid="su-tile-debt" />
        <Tile label="Total Equity" value={m.fmt$(totalEquity)} sub={`${pctOf(totalEquity).toFixed(1)}% of capitalisation`} testid="su-tile-equity" />
      </div>
      <div className="grid g4" style={{ marginBottom: 20 }}>
        <Tile label="Entry Multiple" value={m.fmtX(m.entryMultiple(V))} sub={consolidated ? "Blended across platform + add-ons" : "NAC / EBITDA"} testid="su-tile-mult" />
        <Tile label="Leverage" value={V.sde ? m.fmtX(totalDebt / V.sde) : "—"} sub="Total debt / EBITDA" testid="su-tile-lev" />
        <Tile label="Transaction Costs" value={m.fmt$(tcTotal)} sub={consolidated ? "Platform + add-on fees in Uses" : "Fees + expenses in Uses"} testid="su-tile-costs" />
        <Tile label="DSCR Year 1" value={m.fmtX(m.dscrYr1(V))} tone={m.dscrTone(m.dscrYr1(V))} testid="su-tile-dscr" />
      </div>

      <div className="grid g2">
        <Card title={consolidated ? "Sources & Uses — Consolidated Roll-Up" : "Sources & Uses"} className="table-scroll" testid="su-page-tables">
          <SourcesUsesTables V={V} width={760} />
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }} data-testid="su-right-col">
          <TransactionCostsCard V={V} />
          <CapitalisationCard V={V} style={{ flex: 1 }} />
        </div>
      </div>
    </div>
  );
}
